import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader, EmptyState } from "../components/common";
import { eur, itDate } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Phone, Mail, ChevronRight } from "lucide-react";
import FieldsDialog from "../components/supplier/FieldsDialog";
import { COST_TYPES, label, costText, discountText, effectivePct, today } from "../components/supplier/meta";

const NEW_FIELDS = [{ k: "name", label: "Nome / Ragione sociale *" }, { k: "contact", label: "Referente", half: true }, { k: "phone", label: "Telefono", half: true }, { k: "email", label: "Email" }];
const TYPES = [["sconto", "Sconti"], ...COST_TYPES, ["condizioni", "Condizioni e promozioni"]];

function buildRows(sups, discs, costs) {
  const rows = [];
  discs.forEach((d) => rows.push({ id: d.id, sid: d.supplier_id, type: "sconto", cat: d.product, title: d.product, value: `${discountText(d)}${(d.cascade || []).length ? ` = ${effectivePct(d)}` : ""}`, sub: [d.conditions, d.expiry && `${d.expiry < today() ? "scaduto" : "fino al"} ${itDate(d.expiry)}`].filter(Boolean).join(" · "), tab: "sconti", muted: d.expiry && d.expiry < today() }));
  costs.forEach((c) => rows.push({ id: c.id, sid: c.supplier_id, type: c.type, cat: c.category, title: c.name, value: costText(c), sub: [c.category, c.notes].filter(Boolean).join(" · "), tab: c.type === "trasporto" ? "trasporti" : "imballaggi" }));
  sups.forEach((s) => {
    const add = (title, value, sub = "", muted = false) => rows.push({ id: `${s.id}-${title}-${value}`, sid: s.id, type: "condizioni", cat: "", title, value, sub, tab: "condizioni", muted });
    if (s.payment_terms) add("Pagamento", s.payment_terms);
    if (s.min_order != null) add("Ordine minimo", eur(s.min_order), s.below_min_fee != null ? `sotto soglia: ${eur(s.below_min_fee)}${s.below_min_notes ? ` · ${s.below_min_notes}` : ""}` : "");
    if (s.facilitations) add("Agevolazioni", s.facilitations);
    if (s.conditions) add("Condizioni", s.conditions);
    (s.promotions || []).forEach((p) => add(`Promozione: ${p.title}`, p.expiry ? `fino al ${itDate(p.expiry)}` : "", p.notes, p.expiry && p.expiry < today()));
  });
  return rows;
}

export default function Fornitori() {
  const nav = useNavigate();
  const [tab, setTab] = useState("conditions");
  const [q, setQ] = useState("");
  const [fSup, setFSup] = useState("all");
  const [fCat, setFCat] = useState("all");
  const [fType, setFType] = useState("all");
  const [sups, setSups] = useState([]);
  const [discs, setDiscs] = useState([]);
  const [costs, setCosts] = useState([]);
  const [newDlg, setNewDlg] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/suppliers"), api.get("/discounts"), api.get("/supplier-costs")])
      .then(([a, b, c]) => { setSups(a.data); setDiscs(b.data); setCosts(c.data); }).catch(() => toast.error("Caricamento non riuscito"));
  }, []);

  const rows = useMemo(() => buildRows(sups, discs, costs), [sups, discs, costs]);
  const norm = (c) => (c || "").trim().toLowerCase();
  const cats = useMemo(() => { const m = {}; rows.forEach((r) => { if (norm(r.cat) && !m[norm(r.cat)]) m[norm(r.cat)] = r.cat.trim(); }); return Object.entries(m).sort((a, b) => a[1].localeCompare(b[1])); }, [rows]);
  const s = q.trim().toLowerCase();
  const supName = Object.fromEntries(sups.map((x) => [x.id, x.name]));
  const filtered = rows.filter((r) => (fSup === "all" || r.sid === fSup) && (fType === "all" || r.type === fType) && (fCat === "all" || norm(r.cat) === fCat)
    && (!s || [r.title, r.value, r.sub, r.cat, supName[r.sid]].some((x) => (x || "").toLowerCase().includes(s))));
  const groups = sups.map((x) => [x, filtered.filter((r) => r.sid === x.id)]).filter(([, l]) => l.length);
  const supList = sups.filter((x) => !s || [x.name, x.contact, x.phone, x.email].some((v) => (v || "").toLowerCase().includes(s)));

  const create = async (form) => {
    try { const { data } = await api.post("/suppliers", form); toast.success("Fornitore creato"); setNewDlg(false); nav(`/fornitori/${data.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
  };

  return (
    <div>
      <PageHeader title="Fornitori" subtitle="Anagrafica, condizioni commerciali e costi di acquisto (riservato a titolare e ufficio)">
        <Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="btn-new-supplier" onClick={() => setNewDlg(true)}><Plus size={16} className="mr-1" /> Fornitore</Button>
      </PageHeader>
      <div className="flex flex-wrap gap-2 items-center mb-3">
        {[["conditions", `Condizioni commerciali (${rows.length})`], ["suppliers", `Fornitori (${sups.length})`]].map(([k, l]) => (
          <button key={k} data-testid={`forn-tab-${k}`} onClick={() => setTab(k)} className={`text-sm px-4 py-2 rounded-full border transition-colors ${tab === k ? "bg-brand text-white border-brand" : "bg-white"}`}>{l}</button>
        ))}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="forn-search" className="pl-9" placeholder="Cerca fornitore, prodotto, condizione…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {tab === "conditions" && (<>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
          {[["forn-filter-supplier", fSup, setFSup, "Tutti i fornitori", sups.map((x) => [x.id, x.name])], ["forn-filter-category", fCat, setFCat, "Tutte le categorie", cats], ["forn-filter-type", fType, setFType, "Tutte le tipologie", TYPES]].map(([t, v, set, all, opts]) => (
            <Select key={t} value={v} onValueChange={set}>
              <SelectTrigger data-testid={t}><SelectValue /></SelectTrigger>
              <SelectContent onCloseAutoFocus={(e) => e.preventDefault()} className="max-h-72"><SelectItem value="all">{all}</SelectItem>{opts.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          ))}
        </div>
        {groups.length === 0 ? <EmptyState title="Nessuna condizione trovata" hint="Apri un fornitore per aggiungere sconti, trasporti, imballaggi e condizioni" /> : (
          <div className="space-y-4">
            {groups.map(([x, list]) => (
              <div key={x.id} className="card-soft p-4" data-testid={`cond-group-${x.id}`}>
                <button className="font-head font-bold mb-2 flex items-center gap-1 hover:text-brand" data-testid={`cond-group-open-${x.id}`} onClick={() => nav(`/fornitori/${x.id}`)}>{x.name} <ChevronRight size={16} /></button>
                <div className="space-y-1.5">
                  {list.map((r) => (
                    <button key={r.id} data-testid={`cond-row-${r.id}`} onClick={() => nav(`/fornitori/${x.id}?tab=${r.tab}`)} className={`w-full text-left flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-0.5 p-2.5 rounded-lg bg-slate-50 hover:bg-accent transition-colors ${r.muted ? "opacity-60" : ""}`}>
                      <span className="text-[10px] uppercase font-semibold text-brand sm:w-20 shrink-0 order-1">{label(TYPES, r.type)}</span>
                      <span className="min-w-0 basis-full sm:basis-auto sm:flex-1 order-3 sm:order-2"><span className="text-sm font-medium block truncate">{r.title}</span>{r.sub && <span className="text-xs text-muted-foreground block truncate">{r.sub}</span>}</span>
                      <span className="text-sm font-semibold text-right ml-auto sm:ml-0 sm:max-w-[45%] truncate order-2 sm:order-3">{r.value}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </>)}

      {tab === "suppliers" && (supList.length === 0 ? <EmptyState title="Nessun fornitore" hint="Aggiungi il primo con il pulsante + Fornitore" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {supList.map((x) => {
            const n = rows.filter((r) => r.sid === x.id).length;
            return (
              <button key={x.id} data-testid={`supplier-${x.id}`} onClick={() => nav(`/fornitori/${x.id}`)} className="card-soft p-4 text-left lift">
                <p className="font-head font-bold flex items-center justify-between">{x.name} <ChevronRight size={16} className="text-muted-foreground" /></p>
                {x.contact && <p className="text-xs text-muted-foreground">Ref. {x.contact}</p>}
                <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  {x.phone && <p className="flex items-center gap-1"><Phone size={12} /> {x.phone}</p>}
                  {x.email && <p className="flex items-center gap-1 truncate"><Mail size={12} /> {x.email}</p>}
                </div>
                <p className="text-[11px] text-brand mt-2">{n} condizioni registrate · Apri scheda</p>
              </button>
            );
          })}
        </div>
      ))}

      <FieldsDialog open={newDlg} testid="supplier-dialog" title="Nuovo fornitore" fields={NEW_FIELDS} initial={{}} onClose={() => setNewDlg(false)} onSave={create} />
    </div>
  );
}
